import BroadcastPaceApprovalsPage from './pages/admin/BroadcastPaceApprovalsPage'
import { adminSessionExpired } from './lib/adminSession'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { supabase, ADMIN_EMAIL } from './lib/supabase'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import OrganizationsPage from './pages/OrganizationsPage'
import OrgPulsePage from './pages/OrgPulsePage'
import OrgsListPage from './pages/orgs/OrgsList'
import OrgDetailHebrewPage from './pages/orgs/OrgDetail'
import SupportUsagePage from './pages/SupportUsagePage'
import PlansPage from './pages/PlansPage'
import ProductsPage from './pages/products/ProductsPage'
import JumpstartChatPage from './pages/products/JumpstartChatPage'
import ModulesPage from './pages/modules/ModulesPage'
import ModulesMatrixPage from './pages/modules/ModulesMatrixPage'
import LicensingPage from './pages/licensing/LicensingPage'
import SubscribersPage from './pages/subscribers/SubscribersPage'
import AddonsPage from './pages/addons/AddonsPage'
import AdminAuditPage from './pages/admin/AdminAuditPage'
import ActivityLogPage from './pages/admin/ActivityLogPage'

import UsersPage from './pages/users/UsersPage'

import WhatsAppPage from './pages/WhatsAppPage'
import PartnersPage from './pages/partners/PartnersPage'
import PartnerDetailPage from './pages/partners/PartnerDetailPage'
import BillingPage from './pages/partners/BillingPage'
import SettingsPage from './pages/settings/Settings'
import PricingPage from './pages/whatsapp/PricingPage'
import PaymentCallbackPage from './pages/whatsapp/PaymentCallbackPage'
import PaymentSuccessPage from './pages/whatsapp/PaymentSuccessPage'
import PaymentCancelPage from './pages/whatsapp/PaymentCancelPage'
import PublicLandingPage from './pages/landing/PublicLandingPage'
import ThankYouPage from './pages/landing/ThankYouPage'
import LeadsPage from './pages/leads/LeadsPage'
import LandingPageBuilderPage from './pages/landing-admin/LandingPageBuilder'
import LandingPageDetailPage from './pages/landing-admin/LandingPageDetail'
import LandingPagesListPage from './pages/landing-admin/LandingPagesList'
import SignupStatusPage from './pages/jumpstart/SignupStatusPage'
import MigrationDiscoveryPage from './pages/migration/DiscoveryPage'
import MigrationWizardPage from './pages/migration/WizardPage'
import CouponsPage from './pages/coupons/CouponsPage'
import TrialInvitesPage from './pages/trials/TrialInvitesPage'
import AffiliatesPage from './pages/affiliates/AffiliatesPage'

import AffiliatePartnersPage from './pages/affiliate/PartnersListPage'
import AffiliatePartnerDetailPage from './pages/affiliate/PartnerDetailPage'
import AffiliateTemplatesPage from './pages/affiliate/TemplatesPage'
import AffiliatePayoutsPage from './pages/affiliate/PayoutsPage'
import Layout from './components/Layout'

import CommandHubPage,{LegacyHubRedirect} from './pages/CommandHubPage'

function AuthGuard({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [authed, setAuthed] = useState(false)

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setAuthed(!!data.user && data.user.email === ADMIN_EMAIL)
      setLoading(false)
    })
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthed(!!session?.user && session.user.email === ADMIN_EMAIL)
    })
    const expire = () => { if (adminSessionExpired()) { setAuthed(false); void supabase.auth.signOut({ scope: 'local' }) } }
    const timer = window.setInterval(expire, 30_000)
    window.addEventListener('focus', expire)
    return () => { listener.subscription.unsubscribe(); window.clearInterval(timer); window.removeEventListener('focus', expire) }
  }, [])

  if (loading) return (
    <div className="flex items-center justify-center h-screen" style={{ background: '#0f1117' }}>
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500" />
    </div>
  )
  if (!authed) return <Navigate to="/login" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Command screen verifies canonical session and active-admin membership at its endpoint. */}
        <Route path="/command/*" element={<AuthGuard><CommandHubPage /></AuthGuard>} />
        {/* Public routes — no auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/whatsapp/pricing" element={<PricingPage />} />
        <Route path="/pricing/whatsapp" element={<PricingPage />} />
        <Route path="/payment/callback" element={<PaymentCallbackPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        <Route path="/join/:slug" element={<PublicLandingPage />} />
        <Route path="/join/:slug/thanks" element={<ThankYouPage />} />
        <Route
          path="/*"
          element={
            <AuthGuard>
              <Layout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/leads" element={<LeadsPage />} />
                  <Route path="/landing-pages" element={<LandingPagesListPage />} />
                  <Route path="/landing-pages/new" element={<LandingPageBuilderPage />} />
                  <Route path="/landing-pages/:id" element={<LandingPageDetailPage />} />
                  <Route path="/jumpstart/signups" element={<SignupStatusPage />} />
                  <Route path="/organizations" element={<OrganizationsPage />} />
                  <Route path="/org-pulse" element={<OrgPulsePage />} />
                  <Route path="/orgs" element={<OrgsListPage />} />
                  <Route path="/support" element={<SupportUsagePage />} />
                  <Route path="/orgs/:id" element={<OrgDetailHebrewPage />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/organizations/:id" element={<OrgDetailHebrewPage />} />
                  <Route path="/plans" element={<PlansPage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/jumpstart-chat" element={<JumpstartChatPage />} />
                  <Route path="/modules" element={<ModulesPage />} />
                  <Route path="/modules-matrix" element={<ModulesMatrixPage />} />
                  <Route path="/licensing" element={<LicensingPage />} />
                  <Route path="/subscribers" element={<SubscribersPage />} />
                  <Route path="/addons" element={<AddonsPage />} />
                  <Route path="/coupons" element={<CouponsPage />} />
                  <Route path="/affiliates" element={<AffiliatesPage />} />
                  <Route path="/affiliate/partners" element={<AffiliatePartnersPage />} />
                  <Route path="/affiliate/partners/:id" element={<AffiliatePartnerDetailPage />} />
                  <Route path="/affiliate/templates" element={<AffiliateTemplatesPage />} />
                  <Route path="/affiliate/payouts" element={<AffiliatePayoutsPage />} />
                  <Route path="/trials" element={<TrialInvitesPage />} />
                  <Route path="/migration/discovery" element={<MigrationDiscoveryPage />} />
                  <Route path="/migration/run/:orgId" element={<MigrationWizardPage />} />
                  <Route path="/admin/broadcast-pace-approvals" element={<BroadcastPaceApprovalsPage />} />
                  <Route path="/admin/audit" element={<AdminAuditPage />} />
                  <Route path="/admin/activity-log" element={<ActivityLogPage />} />
                  <Route path="/ai-usage" element={<LegacyHubRedirect />} />
                  <Route path="/monitor" element={<LegacyHubRedirect />} />
                  <Route path="/releases" element={<LegacyHubRedirect />} />
                  <Route path="/health" element={<LegacyHubRedirect />} />
                  <Route path="/services-monitor" element={<LegacyHubRedirect />} />
                  <Route path="/systems" element={<LegacyHubRedirect />} />
                  <Route path="/infrastructure" element={<LegacyHubRedirect />} />
                  <Route path="/whatsapp" element={<WhatsAppPage />} />
                  <Route path="/admin/whatsapp" element={<WhatsAppPage />} />
                  <Route path="/partners" element={<PartnersPage />} />
                  <Route path="/partners/:id" element={<PartnerDetailPage />} />
                  <Route path="/billing" element={<BillingPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            </AuthGuard>
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
